import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Footer from '../components/layout/Footer';
import { MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { ScrollReveal } from '../components/ui/ScrollReveal';

const CONTACT_INFO = {
  address: 'Bisi House, Subhashree Vihar, Near Madanawati Public School, Ainthapali, Sambalpur, Pin - 768004, Odisha',
  phone: '+91 9437082081 / +91 9437071181',
  email: 'bisiengineering@ymail.com',
  businessHours: 'Monday - Saturday: 9:00 AM - 6:00 PM',
};

const WHY_CONTACT_ITEMS = [
  'Free consultation and project assessment',
  'Quick response within 24 hours',
  'Expert advice from experienced engineers',
  'Transparent pricing and timelines',
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      // Success
      setIsSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F2]">
      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="pb-24 pt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header Section */}
          <ScrollReveal variant="fadeUp" threshold={0.2}>
            <div className="mb-16">
              <span className="text-xs tracking-[0.2em] text-[#6B6B65] uppercase">
                Get in Touch
              </span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] mt-2 font-semibold">
                Contact us
              </h1>
              <p className="text-[#6B6B65] text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
                We're here to help you with your project needs. Whether you have questions about our services, need a quote, or want to discuss a potential project, our team is ready to assist you.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form & Get in Touch Section */}
            <div id="contact-form" className="lg:col-span-7">
              <ScrollReveal variant="fadeUp" threshold={0.15}>
                <div className="mb-10">
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] font-semibold mb-3">
                    Get in Touch
                  </h2>
                  <p className="text-[#6B6B65] text-base leading-relaxed">
                    Have a project in mind? We'd love to hear from you. Contact us using the form or reach out directly.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Jane"
                        className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#E0DDD9] text-[#1A1A1A] placeholder-[#8A8884] rounded-lg focus:outline-none focus:border-[#8B7355] focus:ring-1 focus:ring-[#8B7355] transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Smith"
                        className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#E0DDD9] text-[#1A1A1A] placeholder-[#8A8884] rounded-lg focus:outline-none focus:border-[#8B7355] focus:ring-1 focus:ring-[#8B7355] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="jane@example.com"
                        className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#E0DDD9] text-[#1A1A1A] placeholder-[#8A8884] rounded-lg focus:outline-none focus:border-[#8B7355] focus:ring-1 focus:ring-[#8B7355] transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#E0DDD9] text-[#1A1A1A] placeholder-[#8A8884] rounded-lg focus:outline-none focus:border-[#8B7355] focus:ring-1 focus:ring-[#8B7355] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Type something..."
                      className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#E0DDD9] text-[#1A1A1A] placeholder-[#8A8884] rounded-lg focus:outline-none focus:border-[#8B7355] focus:ring-1 focus:ring-[#8B7355] transition-colors resize-y min-h-[120px]"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitted || isLoading}
                    className="px-8 py-3 bg-[#1A1A1A] text-white text-xs font-semibold tracking-widest uppercase rounded-lg hover:bg-[#8B7355] transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : isSubmitted ? (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Message Sent!
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </form>
              </ScrollReveal>
            </div>

            {/* Sidebar - Why Contact Us & Contact Info */}
            <div className="lg:col-span-5 space-y-10">
              {/* Why Contact Us */}
              <ScrollReveal variant="fadeUp" threshold={0.15} delay={0.1}>
                <div>
                  <h3 className="font-serif text-xl text-[#1A1A1A] font-semibold mb-6">
                    Why Contact Us?
                  </h3>
                  <ul className="space-y-4">
                    {WHY_CONTACT_ITEMS.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#8B7355] flex-shrink-0 mt-0.5" />
                        <span className="text-[#6B6B65] text-base leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>

              {/* Contact Info */}
              <ScrollReveal variant="fadeUp" threshold={0.15} delay={0.2}>
                <div className="bg-white rounded-xl border border-[#E0DDD9] p-6 md:p-8">
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-[#8B7355]" />
                        <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider">Address</h4>
                      </div>
                      <p className="text-[#6B6B65] text-sm leading-relaxed pl-6">
                        {CONTACT_INFO.address}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-[#8B7355]" />
                        <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider">Phone</h4>
                      </div>
                      <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="text-[#6B6B65] text-sm hover:text-[#8B7355] transition-colors pl-6 block">
                        {CONTACT_INFO.phone}
                      </a>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-[#8B7355]" />
                        <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider">Email</h4>
                      </div>
                      <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#6B6B65] text-sm hover:text-[#8B7355] transition-colors pl-6 block">
                        {CONTACT_INFO.email}
                      </a>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-[#8B7355]" />
                        <h4 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wider">Business Hours</h4>
                      </div>
                      <p className="text-[#6B6B65] text-sm leading-relaxed pl-6">
                        {CONTACT_INFO.businessHours}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </motion.main>
      <Footer />
    </div>
  );
};

export default ContactPage;
